/*
 * BulkUploadApprovalList.tsx
 * This approval list Component is used for representing all the details of dealer which are about to get uploaded in system.
 */
// Import necessary modules and components
import { useContext, useRef, useState } from "react";
import { Button, Col, Row, Statistic } from "antd";
import useToast from "../../../../hooks/useToast";
import { TResponseModel } from "../../../../types/config";
import { eHTTPStatusCode, eResultCode } from "../../../../utils/enum";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import {
  STAKE_HOLDERS,
  UNAUTHORIZEDREQUEST,
} from "../../../../utils/constants";
import { TBulkUploadResponse } from "../../../../types";
import {
  GetADLErrorReport,
  ImportDealerList,
} from "../../../../utils/api.constant";
import AuthUtil from "../../../../utils/auth";
import {
  CheckOutlined,
  CloseOutlined,
  DownloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { UserContext } from "../../../../context";

type TProps = {
  customerId?: number;
  customerName?: string;
  dealerMasterId?: number;
  refreshADL?: Function;
  bulkUploadResponse?: TBulkUploadResponse;
  reuploadADL?: boolean;
  fileName?: string;
};

export default function BulkUploadApprovalList(props: TProps) {
  // State and hooks initialization
  const [file, setFile] = useState<File | null>(null); // Store the uploaded file
  const [isLoading, setIsLoading] = useState(false);
  const { showToast, onShowToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const context: any = useContext(UserContext);

  const handleDownload = async () => {
    try {
      // Start loading
      setIsLoading(true);

      // Fetch the XLSX file
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${GetADLErrorReport}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json", // Ensure JSON payload
            ...AuthUtil.getToken(),
          },
          body: JSON.stringify({
            data: {
              ADLBulkUploadTempId: props?.bulkUploadResponse?.id,
            },
          }), // Convert body to JSON string
        }
      );

      // Check if the response is OK
      if (response.status === eHTTPStatusCode.OK) {
        // Extract content-disposition header to get the file name
        const contentDisposition = response.headers.get("Content-Disposition");
        let fileName = `${props?.customerName}_ADL_Validated_Report.xlsx`; // Default file name
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch?.[1]) {
            fileName = fileNameMatch[1];
          }
        }

        // Convert response to Blob
        const blob = await response.blob();

        // Create a URL for the Blob
        const url = window.URL.createObjectURL(blob);

        // Create a link element and set the URL as href
        const link = document.createElement("a");
        link.href = url;

        // Set the file name for the download
        link.download = fileName; // Use file name from response header or default

        // Append the link to the document and trigger the download
        document.body.appendChild(link);
        link.click();

        // Clean up by removing the link and revoking the URL
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Show a success toast message
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: "File downloaded successfully!",
        });
      } else if (response.status === eHTTPStatusCode.UNAUTHORIZED_REQUEST) {
        onShowToast({
          type: ToastType.error,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: UNAUTHORIZEDREQUEST,
        });
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content:
            "Error while downloading sample file, please try again later.",
        });
      }
    } catch (error) {
      // Handle any exceptions
      console.error("Download error:", error);
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Error downloading file",
      });
    } finally {
      // Stop loading
      setIsLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];

    if (selectedFile) {
      const isXlsx =
        selectedFile.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      if (!isXlsx) {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: "Please upload an XLSX file!",
        });
        setFile(null); // Reset file state
        event.target.value = ""; // Clear the input
      } else {
        setFile(selectedFile); // Set the selected file
        handleSubmit(selectedFile);
      }
    }

    // Reset the input value to ensure `onChange` is triggered for the same file
    event.target.value = "";
  };

  /**
   * Handles the submission of the bulk upload form.
   * Validates the file and if valid, sends a POST request to the server to process the file.
   * If the response is successful, refreshes the list and closes the drawer.
   * If the response is not successful, shows an error toast with the error message.
   */
  const handleSubmit = async (selectedFile: any) => {
    if (!selectedFile) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Please select an XLSX file!",
      });
      return;
    }

    // Implement your file upload logic here
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("customerId", props?.customerId?.toString() || "");

    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_PUBLIC_API_BASEURL}${ImportDealerList}`,
        {
          method: "POST",
          body: formData,

          headers: {
            ...AuthUtil.getToken(),
          },
        }
      );
      if (response.status === eHTTPStatusCode.OK) {
        const responseData = (await response.json()) as TResponseModel;

        if (responseData.dataResponse.returnCode === eResultCode.SUCCESS) {
          props?.refreshADL?.(responseData.data);
        } else {
          onShowToast({
            type: ToastType.error,
            title: <CloseOutlined />,
            position: ToastOpen.centerTop,
            content: responseData.dataResponse.description,
          });
          // errorReportDownload(responseData.data);
        }
      } else if (response.status === eHTTPStatusCode.UNAUTHORIZED_REQUEST) {
        onShowToast({
          type: ToastType.error,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: UNAUTHORIZEDREQUEST,
        });
      }
    } catch (error) {
      onShowToast({
        type: ToastType.error,
        title: <CloseOutlined />,
        position: ToastOpen.centerTop,
        content: "Error uploading file",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Function to trigger file input click
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // Return JSX structure for User Component
  return (
    <div className="">
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "1rem",
          // padding: "0 1rem",
        }}
      >
        <div style={{ display: "flex", gap: "1rem" }}>
          {/* <Form.Item label="" style={{ marginBottom: 0 }}> */}
          {props?.reuploadADL ? (
            <div>
              <input
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                ref={fileInputRef}
                style={{ display: "none" }} // Hide the input
              />
              <Button
                onClick={triggerFileInput}
                icon={<UploadOutlined />}
                style={{
                  width: "-webkit-fill-available",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                {file
                  ? `${file.name.substring(0, 28)}...`
                  : "Upload Verified ADL"}
              </Button>
            </div>
          ) : null}

          {/* </Form.Item> */}
          <div>
            <Button
              disabled={isLoading}
              type="primary"
              style={{ marginRight: "10px" }}
              onClick={handleDownload}
            >
              <DownloadOutlined style={{ fontSize: "22px" }} />
              Validated ADL
            </Button>
          </div>
        </div>
      </div>
      <div style={{ marginTop: "2rem" }}>
        {props?.fileName ? (
          <Row gutter={16} style={{ marginBottom: "20px" }}>
            <Col span={24}>
              <Statistic
                title="Uploaded File Name"
                valueRender={() => <span>{props?.fileName}</span>}
              />
            </Col>
          </Row>
        ) : null}

        <Row gutter={16} style={{ marginBottom: "20px" }}>
          <Col span={12}>
            <Statistic
              title="Total Records"
              value={props?.bulkUploadResponse?.all}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title="Error Records"
              value={props?.bulkUploadResponse?.error}
            />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Statistic
              title="Updated Records"
              value={props?.bulkUploadResponse?.replaced}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title="Unfailing Records"
              value={props?.bulkUploadResponse?.unfailing}
            />
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: "10px" }}>
          <Col span={24}>
            {props?.bulkUploadResponse?.error ? (
              <span style={{ color: "red" }}>
                {`Note: We found errors in 
                ${props?.bulkUploadResponse?.error} records. To review the detailed list of errors, please download the updated file by clicking the 'Validated ADL' button. After correcting the errors, re-upload the file to proceed.`}
              </span>
            ) : (
              <>
                {props?.reuploadADL ? (
                  <span style={{ color: "var(--bs-primary-dark)" }}>
                    The file has been successfully validated without any errors.
                    Please download and verify the file by clicking the
                    Validated ADL button before proceeding.
                    <br />
                    <br />
                    <small style={{ color: "red" }}>
                      Note: Ensure the file is thoroughly verified before
                      approval to save the changes.
                    </small>
                  </span>
                ) : (
                  <span style={{ color: "var(--bs-primary-dark)" }}>
                    {`The file has been successfully validated without any errors. Please click ${
                      context?.user?.stakeHolderName === STAKE_HOLDERS.CUSTOMER
                        ? "Submit to allow Idex to verify and approve the list"
                        : "Approve to save the changes"
                    } to proceed.`}
                  </span>
                )}
              </>
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
}
